import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { INTERESTS, DISLIKES, VIBES, DIETS, ACCESS, BUDGETS, TRANSPORT } from "@/lib/constants";
import { Chip, FieldLabel, slug } from "@/components/Chip";
import { StartPicker } from "@/components/StartPicker";

const DEFAULTS = { interests: [], dislikes: [], vibe: [], diet: [], accessibility: [], activity_level: "moderate", pace: "relaxed",
  budget: "low", transport: "walk", max_distance_km: 3, home: null, weekend_nudge: true, favorite_neighborhoods: "" };

const Multi = ({ options, value, onChange, prefix, tone }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <Chip key={o} tone={tone} testId={`${prefix}-${slug(o)}`} active={value.includes(o)}
        onClick={() => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o])}>{o}</Chip>
    ))}
  </div>
);

const Single = ({ options, value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <Chip key={o.v} tone="forest" testId={`${prefix}-${o.v}`} active={value === o.v} onClick={() => onChange(o.v)}>{o.l}</Chip>
    ))}
  </div>
);

export default function Onboarding({ edit }) {
  const { user, setUser } = useAuth();
  const [p, setP] = useState({ ...DEFAULTS, ...(user?.profile || {}) });
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const set = (k) => (v) => setP((x) => ({ ...x, [k]: v }));

  const steps = [
    { t: "What do you enjoy?", d: "Pick anything that sounds like a good time.", body: <Multi options={INTERESTS} value={p.interests} onChange={set("interests")} prefix="interest" /> },
    { t: "What's not for you?", d: "We'll steer around these. And what vibe do you like?", body: (
      <div className="space-y-6"><div><FieldLabel>Dislikes</FieldLabel><Multi options={DISLIKES} value={p.dislikes} onChange={set("dislikes")} prefix="dislike" tone="forest" /></div>
        <div><FieldLabel>Vibe</FieldLabel><Multi options={VIBES} value={p.vibe} onChange={set("vibe")} prefix="vibe" /></div></div>) },
    { t: "Food & accessibility", d: "Optional. Only used to shape your suggestions.", body: (
      <div className="space-y-6"><div><FieldLabel>Dietary needs</FieldLabel><Multi options={DIETS} value={p.diet} onChange={set("diet")} prefix="diet" /></div>
        <div><FieldLabel>Accessibility needs</FieldLabel><Multi options={ACCESS} value={p.accessibility} onChange={set("accessibility")} prefix="access" tone="forest" /></div></div>) },
    { t: "Your pace", d: "Packed day or slow wander? Both are great.", body: (
      <div className="space-y-6"><div><FieldLabel>Activity level</FieldLabel><Single options={[{ v: "low", l: "Easygoing" }, { v: "moderate", l: "Moderate" }, { v: "high", l: "Active" }]} value={p.activity_level} onChange={set("activity_level")} prefix="activity" /></div>
        <div><FieldLabel>Pace</FieldLabel><Single options={[{ v: "relaxed", l: "Relaxed, fewer stops" }, { v: "balanced", l: "Balanced" }, { v: "packed", l: "Packed, see a lot" }]} value={p.pace} onChange={set("pace")} prefix="pace" /></div>
        <div><FieldLabel>Favorite neighborhoods (optional)</FieldLabel><input data-testid="profile-neighborhoods-input" value={p.favorite_neighborhoods} onChange={(e) => set("favorite_neighborhoods")(e.target.value)} placeholder="e.g. South Congress, East Austin" className="w-full h-10 rounded-lg border border-stone-300 px-3 text-sm" /></div></div>) },
    { t: "Your defaults", d: "Used for one-tap outings. You can change them on every plan.", body: (
      <div className="space-y-6">
        <div><FieldLabel>Typical budget per person</FieldLabel><Single options={BUDGETS} value={p.budget} onChange={set("budget")} prefix="profile-budget" /></div>
        <div><FieldLabel>Getting around</FieldLabel><Single options={TRANSPORT} value={p.transport} onChange={set("transport")} prefix="profile-transport" /></div>
        <div><FieldLabel>Max travel distance: {p.max_distance_km} km</FieldLabel><Slider data-testid="profile-distance-slider" min={0.5} max={8} step={0.5} value={[p.max_distance_km]} onValueChange={([v]) => set("max_distance_km")(v)} /></div>
        <div><FieldLabel>Home area (private, never shown to friends)</FieldLabel><StartPicker value={p.home} onChange={set("home")} testPrefix="profile-home" /></div>
        <label className="flex items-center justify-between gap-4 rounded-xl border border-stone-200 bg-white p-4">
          <span><span className="font-semibold text-sm">"Free this weekend?" suggestions</span><span className="block text-xs text-stone-500">A friendly in-app idea now and then. No streaks, no guilt.</span></span>
          <Switch data-testid="profile-weekend-nudge-switch" checked={p.weekend_nudge} onCheckedChange={set("weekend_nudge")} />
        </label>
      </div>) },
  ];

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/profile", { profile: p });
      setUser(data);
      toast.success(edit ? "Taste profile updated" : "You're all set. Let's get you out there.");
      nav("/");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const s = steps[step];
  const last = step === steps.length - 1;
  return (
    <div className="max-w-2xl mx-auto px-5 py-10 lg:py-16" data-testid="onboarding-page">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#23603F]">{edit ? "Taste profile" : "Welcome"} · step {step + 1} of {steps.length}</p>
      <div className="mt-3 h-1.5 bg-stone-200 rounded-full overflow-hidden"><div className="h-full bg-[#2F7D55] transition-[width] duration-300" style={{ width: `${((step + 1) / steps.length) * 100}%` }} /></div>
      <div key={step} className="rise mt-8">
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">{s.t}</h1>
        <p className="mt-2 text-stone-600">{s.d}</p>
        <div className="mt-8">{s.body}</div>
      </div>
      <div className="mt-10 flex items-center justify-between">
        <button data-testid="onboarding-back-btn" disabled={step === 0} onClick={() => setStep(step - 1)} className="px-4 py-2 rounded-xl font-semibold text-stone-600 hover:bg-stone-100 disabled:opacity-30">Back</button>
        <div className="flex gap-2">
          {edit && !last && <button data-testid="onboarding-save-now-btn" onClick={save} disabled={busy} className="px-4 py-2.5 rounded-xl font-semibold border border-stone-300 hover:bg-stone-100">Save</button>}
          <button data-testid={last ? "onboarding-finish-btn" : "onboarding-next-btn"} onClick={last ? save : () => setStep(step + 1)} disabled={busy}
            className="bg-[#2F7D55] hover:bg-[#23603F] text-white font-semibold px-6 py-2.5 rounded-xl transition-colors active:scale-95 disabled:opacity-50">
            {last ? (edit ? "Save profile" : "Start exploring") : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}
